"use client"

import type { ReactNode } from "react"

import { cn } from "@ibada/ui"

export type OverviewRecentRowsTableTone = "income" | "expense" | "neutral" | "muted"

export type OverviewRecentRowsTableRow = {
  id: string
  dateLabel: string
  label: string
  valueLabel: string
  valueTone?: OverviewRecentRowsTableTone
  leadingSlot?: ReactNode
  onSelect?: () => void
}

export type OverviewRecentRowsTableProps = {
  rows: OverviewRecentRowsTableRow[]
  maxRows?: number
  /**
   * Smaller type and tighter rows, for a list sharing a board with single-figure widgets.
   *
   * Also drops the leading column's fixed width: at `w-11` it fits a date, which is what this list was built
   * for, and a word like "Warning" simply ran into the label beside it.
   */
  compact?: boolean
  className?: string
}

function valueToneClass(tone: OverviewRecentRowsTableTone = "neutral"): string {
  if (tone === "income") return "text-emerald-600 dark:text-emerald-400"
  if (tone === "expense") return "text-rose-600 dark:text-rose-400"
  if (tone === "muted") return "text-muted-foreground"
  return "text-foreground"
}

/**
 * A compact recent-rows list (date · label · value). Flex rows — not a table — so the label takes the full
 * remaining width (`flex-1 min-w-0`) and truncates only when genuinely too long, instead of a fixed-width
 * table cell that clips short labels while leaving the row half empty.
 */
export function OverviewRecentRowsTable({
  rows,
  maxRows = 6,
  compact = false,
  className,
}: OverviewRecentRowsTableProps) {
  const textSize = compact ? "text-xs" : "text-sm"
  return (
    <ul className={cn("divide-y divide-border/60", className)}>
      {rows.slice(0, maxRows).map((row) => (
        <li key={row.id}>
          <button
            type="button"
            onClick={row.onSelect}
            disabled={!row.onSelect}
            className={cn(
              "flex w-full items-center gap-3 text-left",
              compact ? "gap-2 py-1.5" : "py-2",
              row.onSelect && "cursor-pointer transition-colors hover:text-primary",
            )}
          >
            <span
              className={cn(
                "shrink-0 tabular-nums text-muted-foreground",
                textSize,
                compact ? "w-auto" : "w-11",
              )}
            >
              {row.dateLabel}
            </span>
            <span className="flex min-w-0 flex-1 items-center gap-2">
              {row.leadingSlot ? <span className="shrink-0">{row.leadingSlot}</span> : null}
              <span className={cn("truncate font-medium", textSize)}>{row.label}</span>
            </span>
            <span
              className={cn(
                "shrink-0 whitespace-nowrap text-right font-medium tabular-nums",
                textSize,
                valueToneClass(row.valueTone),
              )}
            >
              {row.valueLabel}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}
